<template>
  <div class="py-2" v-if="agent_id">

    <label :for="label" class="block text-xss font-medium text-gray-700">{{ label }}</label>
    <div v-if="description" class="text-gray-500 text-xs">
      {{ description }}
    </div>

    <Combobox v-model="selected">
      <div class="relative">
        <div class="relative">
          <ComboboxButton v-if="selected" @click="unSelected()" class="absolute right-8 top-3 z-10">
            <XMarkIcon class="h-4 w-4 cursor-pointer text-gray-400 hover:text-gray-600" />
          </ComboboxButton>
          <ComboboxInput
            class="bg-white relative w-full border border-gray-300 rounded-md shadow-sm pl-3 pr-10 py-2 text-left cursor-default focus:outline-none focus:ring-1 focus:ring-slate-500 focus:border-slate-500 sm:text-xss"
            :displayValue="(item) => item ? item.name : ''" 
            @change="searchTo"
            :placeholder="'Selecione um usuário'" />

          <ComboboxButton class="absolute inset-y-0 right-0 flex items-center pr-2">
            <ArrowsUpDownIcon class="h-5 w-5 text-gray-400" aria-hidden="true" />
          </ComboboxButton>
        </div>
        <TransitionRoot leave="transition ease-in duration-100" leaveFrom="opacity-100" leaveTo="opacity-0"
          @after-leave="query = ''">
          <ComboboxOptions
            class="z-10 absolute mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none text-xss">
            <div v-if="filteredDataRows.length === 0 && query !== ''"
              class="relative cursor-default select-none py-2 px-4 text-gray-700">
              Nenhum usuário encontrado
            </div>
            
            <ComboboxOption v-for="item in filteredDataRows" as="template" :key="item.id" :value="item"
              v-slot="{ selected, active }">
              <li @click="update(item)" class="relative cursor-default select-none py-2 pl-10 pr-4"
                :class="{ 'bg-slate-600 text-white': active, 'text-gray-900': !active, }">
                <span class="block truncate flex items-center" :class="{ 'font-medium': selected, 'font-normal': !selected }">
                  <div v-if="item.avatar && item.avatar.trim()" class="flex-shrink-0 mr-2">
                    <img :src="item.avatar" class="w-5 h-5 rounded-full" :alt="item.name">
                  </div>
                  <div v-else class="flex-shrink-0 mr-2">
                    <div class="w-5 h-5 rounded-full flex items-center justify-center text-xs text-white font-medium"
                         :class="getUserColor(item)">
                      {{ getUserInitial(item) }}
                    </div>
                  </div>
                  <span class="truncate">
                    {{ item.name }}
                    <span v-if="item.email" class="ml-2 text-gray-500 text-xs">
                      ({{ item.email }})
                    </span>
                  </span>
                </span>
                <span v-if="selected" class="absolute inset-y-0 left-0 flex items-center pl-3"
                  :class="{ 'text-white': active, 'text-teal-600': !active }">
                  <CheckIcon class="h-5 w-5" aria-hidden="true" />
                </span>
              </li>
            </ComboboxOption>
          </ComboboxOptions>
        </TransitionRoot>
      </div>
    </Combobox>
    <p v-if="error" class="mt-2 text-sm text-rose-600">Usuário é obrigatório</p>
  </div>
</template>

<script>
import {
  Combobox,
  ComboboxInput,
  ComboboxButton,
  ComboboxOptions,
  ComboboxOption,
  TransitionRoot,
} from '@headlessui/vue'

import { CheckIcon, ArrowsUpDownIcon, XMarkIcon } from '@heroicons/vue/24/solid'

export default {
  name: 'DropDownSearchAgentUsers',
  props: {
    label: String,
    description: String,
    agent_id: String,
    value: [String, Object, Boolean],
    error: String,
    urlDefault: {
      type: String,
      default: null,
      required: false
    },
    fields: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      urlUsers: this.urlDefault || 'core/components/fields/agent_users',
      dataRows: [],
      query: '',
      selected: null,
      params: {
        agent_id: '',
      },
    }
  },
  components: {
    Combobox,
    ComboboxInput,
    ComboboxButton,
    ComboboxOptions,
    ComboboxOption,
    TransitionRoot,
    CheckIcon,
    ArrowsUpDownIcon,
    XMarkIcon,
  },
  methods: {
    getUsers() {
      this.params.fields = this.fields;

      this.$ApiService.post(this.urlUsers, this.params).then(response => {
        // Acessar os dados na estrutura correta: response.data.rows
        this.dataRows = response.data.rows || [];

        // Usuário selecionado (value)
        this.selected = null;
        if (this.value) {
          for (let index = 0; index < this.dataRows.length; index++) {
            const element = this.dataRows[index];
            if (element.id == this.value) {
              this.selected = element;
              break;
            }
          }
        }
      }).catch(error => {
        console.error('Erro ao buscar usuários do agent:', error);
        this.dataRows = [];
        this.selected = null;
      });
    },

    searchTo($event) {
      this.query = $event.target.value
    },

    unSelected() {
      this.selected = null;
      this.$emit('update:modelValue', null);
    },

    update(item) {
      this.selected = item;
      this.$emit('update:modelValue', item ? item.id : null);
    },

    search(item) {
      return (item.name||"").toLowerCase().indexOf((this.query||"").toLowerCase()) > -1 ||
             (item.email && (item.email||"").toLowerCase().indexOf((this.query||"").toLowerCase()) > -1)
    },

    getUserColor(item) {
      // Se tem cor definida, usar ela
      if (item.color) {
        return item.color;
      }
      
      // Se não tem cor, gerar uma baseada no nome
      const colors = [
        'bg-red-500', 'bg-blue-500', 'bg-green-500', 'bg-yellow-500', 
        'bg-purple-500', 'bg-pink-500', 'bg-indigo-500', 'bg-teal-500',
        'bg-orange-500', 'bg-cyan-500', 'bg-lime-500', 'bg-emerald-500'
      ];
      
      // Gerar hash simples baseado no nome
      const hash = item.name ? item.name.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0) : 0;
      return colors[hash % colors.length];
    },

    getUserInitial(item) {
      return item.name ? item.name.charAt(0).toUpperCase() : '?';
    },
  },

  computed: {
    filteredDataRows() {
      // Impede que sejam mostrados dados (apenas não retorna nada)
      if (!this.dataRows) {
        return []
      }

      return this.dataRows.filter(item => {
        if (this.query === '') {
          return true;
        }
        return this.search(item);
      });
    },
  },

  watch: {
    agent_id(newValue) {
      this.params.agent_id = newValue;
      this.selected = null;
      this.$emit('update:modelValue', null);
      if (newValue) {
        this.getUsers();
      }
    },
  },

  created() {
    this.params.agent_id = this.agent_id;
    this.selected = null;
    if (this.agent_id) {
      this.getUsers();
    }
  },
}
</script> 